import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  DashboardEntry,
  DashboardStatsData,
  useDashboardStats,
  variantKey,
} from "../store/use-dashboard-stats";
import UseGraph from "./useGraph";

const EMPTY: DashboardEntry = {
  data: null,
  computedAt: null,
  stale: false,
  loading: true,
  refreshing: false,
  error: null,
  errors: [],
};

/**
 * Reads the cached dashboard bundle for the currently selected filters.
 *
 * `subscribe: false` for the chart components — only the page shell should trigger
 * the fetch, otherwise seven subscribers would each kick off a request for the same
 * variant on mount.
 */
export default function useDashboardData(subscribe = false) {
  const { startDate, endDate, isRestarted } = UseGraph();
  const entries = useDashboardStats((s) => s.entries);
  const fetchBundle = useDashboardStats((s) => s.fetch);
  const stopAll = useDashboardStats((s) => s.stopAll);

  const variant = useMemo(
    () => ({
      startDate: startDate ?? null,
      endDate: endDate ?? null,
      isRestarted: Boolean(isRestarted),
    }),
    [startDate, endDate, isRestarted],
  );

  const key = variantKey(variant);
  const entry = entries[key] ?? EMPTY;

  useEffect(() => {
    if (!subscribe) return;
    // Already cached in-store for this variant — navigating back is instant. A poll cut
    // short by leaving the page left it "refreshing", so that one is fetched again.
    if (entries[key]?.data && !entries[key]?.refreshing) return;
    fetchBundle(variant);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, subscribe]);

  // polling belongs to the dashboard, leaving it must not keep hitting /stats/dashboard
  useEffect(() => {
    if (!subscribe) return;
    return () => stopAll();
  }, [subscribe, stopAll]);

  /**
   * Last successfully loaded bundle, kept across variant changes.
   *
   * Every new filter combination is a cold cache miss, and a cold bundle can take
   * a while to build. Dropping straight to `null` blanked every chart the instant you
   * touched the toggle or the calendar, which looked like the controls were broken.
   * We keep the previous numbers on screen (flagged as stale via `showingPrevious`,
   * so the page can dim them) until the new variant arrives.
   */
  const lastGood = useRef<DashboardStatsData | null>(null);
  if (entry.data) lastGood.current = entry.data;

  const data = entry.data ?? lastGood.current;
  const showingPrevious = !entry.data && Boolean(lastGood.current);

  const refresh = useCallback(
    () => fetchBundle(variant, { force: true }),
    [fetchBundle, variant],
  );

  return {
    ...entry,
    data,
    /** True when the charts on screen belong to the *previous* filter selection. */
    showingPrevious,
    /** No data at all has ever loaded — show skeletons rather than stale numbers. */
    isEmpty: !data,
    refresh,
  };
}
